import { Injectable } from '@angular/core';

import { AuthService } from '../../features/auth/services/auth.service';
import { AbilityService } from './ability/ability.service';
import { SsoScopeService } from './sso/sso-scope.service';
import { StorageBrowser } from './storage.service';
import { UserService } from './user.service';

const ACCESS_TOKEN_FRAGMENT_KEY = 'access_token';

@Injectable({ providedIn: 'root' })
export class SsoBootstrapService {
  constructor(
    private storageBrowser: StorageBrowser,
    private userService: UserService,
    private authService: AuthService,
    private ability: AbilityService,
    private ssoScope: SsoScopeService,
  ) {}

  /**
   * Reads `#access_token=<jwt>` set by the receiving lab's GET /api/sso
   * 302 redirect, stores the token, strips the fragment from the URL, and
   * kicks off a /users/me fetch to populate the local user state.
   *
   * Returns true if a token fragment was consumed. Call once at app init,
   * before any code reads the token from storage.
   *
   * The T&C dialog is deliberately not opened here. The top-level
   * acceptance belongs to sso-fe — if the user had never accepted, the
   * sso-fe sign-in page already blocked their departure for the Lab until
   * they did, and the Lab callback has already stamped this Lab's own
   * `terms_accepted_at` to inherit that acceptance. Firing a second
   * dialog on arrival would ask a question the user has already answered.
   */
  public bootstrap(): boolean {
    const hash = window.location.hash || '';
    if (!hash.includes(`${ACCESS_TOKEN_FRAGMENT_KEY}=`)) {
      return false;
    }

    const params = new URLSearchParams(hash.slice(1));
    const accessToken = params.get(ACCESS_TOKEN_FRAGMENT_KEY);
    if (!accessToken) {
      return false;
    }

    this.storageBrowser.set('token', accessToken);
    this.storageBrowser.set('last_active', Date.now());

    // Sticky SSO marker: write SYNCHRONOUSLY before /me fires so guards
    // see isSSO=true on the first navigation (avoids the race where
    // PermissionGuard runs against stale ability data and bounces).
    // `entryUrl` is the rdr path the BE 302'd us to (e.g. /working-area/<id>/report).
    // `mainLabUrl` is filled in when /me resolves.
    const entryUrl = window.location.pathname + window.location.search;
    this.ssoScope.set({ entryUrl });

    // Strip the fragment so it doesn't linger in address bar / screenshots.
    history.replaceState(null, '', entryUrl);

    this.authService.me().subscribe({
      next: (user) => {
        this.storageBrowser.set('user', user);
        this.userService.currentUser.next(user);
        this.ssoScope.setMainLabUrl(user?.mainLabUrl ?? null);
        this.ability.refresh();
      },
      error: () => {
        // /me failed — the JWT itself is still valid for API calls and
        // sso_scope is already set, so the kiosk UI and scope guard stay
        // intact. Next authenticated call will hit the standard 401 →
        // refresh flow if the token actually went bad.
      },
    });

    return true;
  }
}
